/**
 * B3tter scroll motion: reveal-on-scroll, staggered block entrances, hero parallax and stat count-ups.
 *
 * Elements are picked up automatically inside #MainContent, so sections added in the theme editor
 * animate without extra setup. Add `data-reveal` (optionally ="left", "right" or "scale") to any element
 * to opt it in explicitly, or `data-no-reveal` to opt out.
 *
 * Skipped entirely when the visitor prefers reduced motion or in the theme editor (see snippets/b3tter-motion.liquid).
 */
(function () {
  const root = document.documentElement;
  // Decorative movement only runs with .b3-motion (absent for reduced motion); the behaviours below
  // that change what is shown (active list row, lit pinned word, the buy stub) run for everyone.
  const motion = root.classList.contains('b3-motion');
  if (motion) window.B3Motion = true;

  const main = document.getElementById('MainContent');
  if (!main) return;

  const AUTO_TARGETS = [
    // Blocks inside Horizon's generic sections (text, buttons, groups/cards).
    '.section-content-wrapper > *',
    // Whole sections that don't use the generic wrapper (product grids, marquees, custom sections).
    '.shopify-section:not(:first-child) .resource-list__item',
    '.shopify-section:not(:first-child) marquee-component',
    '.b3-events__header',
    '.b3-impact__header',
    // Redesign sections: each part enters on its own, staggered within its parent.
    '.b3-hero__card > *',
    '.b3-pinned__piece',
    '.b3-closeup__card > *',
    '.b3-verbs__note',
    '.b3-figure__claim',
    '.b3-figure__bottom > *',
    '.b3-index__heading',
    '.b3-index__row',
    '.b3-close__text > *',
    '.b3-footer__row > *',
    // v2 (monochrome) sections.
    '.b3v-hero__content > *',
    '.b3v-head',
    '.b3v-shop__item',
    '.b3v-steps__card',
    '.b3v-cards__card',
    '.b3v-stats__item',
    '.b3v-faq__item',
    '.b3v-duo__card',
  ].join(',');

  const STAGGER_MS = 90;
  const MAX_STAGGER_MS = 450;

  function collectTargets() {
    const targets = new Set(main.querySelectorAll('[data-reveal]'));
    main.querySelectorAll(AUTO_TARGETS).forEach((el) => {
      if (el.closest('[data-no-reveal]')) return;
      // Don't double-animate children of an element that already reveals.
      if (el.parentElement && el.parentElement.closest('[data-reveal]')) return;
      // Hero content is above the fold: it gets a gentle entrance on load instead.
      if (el.closest('.hero')) return;
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', '');
      targets.add(el);
    });
    return [...targets].filter((el) => !el.classList.contains('is-revealed'));
  }

  function staggerSiblings(targets) {
    const groups = new Map();
    targets.forEach((el) => {
      const key = el.parentElement;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(el);
    });
    groups.forEach((items) => {
      items.forEach((el, i) => {
        el.style.setProperty('--b3-reveal-delay', `${Math.min(i * STAGGER_MS, MAX_STAGGER_MS)}ms`);
      });
    });
  }

  /* Count-up for impact numbers */
  function formatNumber(n) {
    return Math.round(n).toLocaleString('en-US');
  }

  function countUp(el) {
    const target = Number(el.dataset.countTo);
    if (!Number.isFinite(target) || target <= 0 || el.dataset.counted) return;
    el.dataset.counted = 'true';
    const prefix = el.dataset.prefix || '';
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = prefix + formatNumber(target * eased);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        countUp(entry.target);
        counterObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.6 }
  );

  /* Reveal */
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );

  function init() {
    const targets = collectTargets();
    staggerSiblings(targets);
    targets.forEach((el) => revealObserver.observe(el));
    main.querySelectorAll('[data-count-to]').forEach((el) => {
      // Band numbers are duplicated by the marquee; count them all together.
      counterObserver.observe(el);
    });
    root.classList.add('b3-motion-ready');
  }

  /* Horizon scrolls inside .page-wrapper rather than the window, so listen in the capture phase to catch any scroller. */
  function onScroll(handler) {
    document.addEventListener('scroll', handler, { passive: true, capture: true });
  }

  /* Hero parallax: the background media drifts slower than the page. */
  function initParallax() {
    const media = main.querySelector('.shopify-section:first-child .hero__media-grid');
    if (!media) return;
    const hero = media.closest('.hero');
    media.classList.add('b3-parallax');
    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = hero.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const offset = Math.max(0, -rect.top) * 0.3;
      media.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0) scale(1.08)`;
    };

    onScroll(() => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    });
    update();
  }

  /* Large section headings drift slightly as they pass through the viewport. */
  function initHeadingShift() {
    const headings = main.querySelectorAll(
      '.shopify-section:not(:first-child) .section-content-wrapper > .text-block h1, .shopify-section:not(:first-child) .section-content-wrapper > .text-block h2, .b3-events__header h2, .b3-impact__header h2'
    );
    if (!headings.length) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      headings.forEach((h) => {
        const rect = h.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > vh) return;
        // -1 when entering at the bottom, 1 when leaving at the top.
        const progress = 1 - (rect.top + rect.height / 2) / (vh / 2);
        h.style.setProperty('--b3-shift', `${(progress * -12).toFixed(1)}px`);
      });
    };
    headings.forEach((h) => h.classList.add('b3-shift'));
    onScroll(() => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    });
    update();
  }

  /* Hero card: shrinks toward the bottom-left as the hero scrolls away; then the stub keeps the action on screen. */
  function initHeroCard() {
    const hero = main.querySelector('[data-b3-hero]');
    const card = hero?.querySelector('[data-b3-hero-card]');
    const stub = document.querySelector('[data-b3-stub]');
    if (!hero || !card) return;
    // The stub hides while any section that owns the bottom-left corner is on screen.
    const hideZones = document.querySelectorAll('[data-b3-stub-hide]');
    const hiding = new Set();
    let ticking = false;

    const setStub = (visible) => {
      if (!stub) return;
      stub.classList.toggle('is-visible', visible);
      stub.setAttribute('aria-hidden', visible ? 'false' : 'true');
      stub.tabIndex = visible ? 0 : -1;
    };

    const update = () => {
      ticking = false;
      const rect = hero.getBoundingClientRect();
      const progress = Math.min(Math.max(-rect.top / rect.height, 0), 1);
      if (motion) card.style.transform = progress > 0 ? `scale(${(1 - progress * 0.4).toFixed(3)})` : '';
      setStub(rect.bottom < 0 && hiding.size === 0);
    };

    if (hideZones.length) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? hiding.add(entry.target) : hiding.delete(entry.target)));
        update();
      });
      hideZones.forEach((zone) => observer.observe(zone));
    }
    onScroll(() => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    });
    update();
  }

  /* Street / Shore: the pinned word matching the panel at the middle of the screen turns full ink. */
  function initPinned() {
    main.querySelectorAll('[data-b3-pinned]').forEach((section) => {
      const words = section.querySelectorAll('[data-word]');
      const panels = section.querySelectorAll('[data-terrain]');
      if (!words.length || !panels.length) return;
      const activate = (terrain) => words.forEach((w) => w.classList.toggle('is-active', w.dataset.word === terrain));
      activate(panels[0].dataset.terrain);
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && activate(entry.target.dataset.terrain)),
        { rootMargin: '-45% 0px -45% 0px' }
      );
      panels.forEach((panel) => observer.observe(panel));
    });
  }

  /* The full drop: hover, focus or the row at mid-screen becomes active and swaps the sticky photograph. */
  function initIndex() {
    main.querySelectorAll('[data-b3-index]').forEach((section) => {
      const rows = [...section.querySelectorAll('.b3-index__row')];
      const images = [...section.querySelectorAll('.b3-index__img')];
      if (!rows.length) return;
      const activate = (row) => {
        const index = row.dataset.index;
        rows.forEach((r) => r.classList.toggle('is-active', r === row));
        images.forEach((img) => img.classList.toggle('is-active', img.dataset.index === index));
      };
      section.setAttribute('data-ready', '');
      activate(rows[0]);
      rows.forEach((row) => {
        row.addEventListener('mouseenter', () => activate(row));
        row.addEventListener('focusin', () => activate(row));
      });
      const observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => entry.isIntersecting && activate(entry.target)),
        { rootMargin: '-50% 0px -50% 0px' }
      );
      rows.forEach((row) => observer.observe(row));
    });
  }

  if (motion) {
    init();
    initParallax();
    initHeadingShift();
  }
  initHeroCard();
  initPinned();
  initIndex();

  // Pick up content Horizon swaps in later (filters, infinite scroll, section rendering).
  if (!motion) return;
  let pending = false;
  new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const fresh = collectTargets();
      staggerSiblings(fresh);
      fresh.forEach((el) => revealObserver.observe(el));
    });
  }).observe(main, { childList: true, subtree: true });
})();
